/**
 * Разговоры с ассистентом: всё, кроме самого хода.
 *
 * Ход отдаётся потоком и живёт в stream.ts — здесь обычные вызовы через общий
 * axios: тред заводится один раз на контекст, а остановка это короткий POST,
 * которому поток не нужен.
 */
import { assistantApi, http } from '@/api'
import { TrackerAssistantContextType, TrackerAssistantRole } from '@/api/clients/tracker-assistant'

import { threadPath } from './stream'

export type AssistantContextType = TrackerAssistantContextType

/**
 * Где находится человек, когда спрашивает.
 *
 * Дескриптор нарочно лёгкий: тип, ключ и подпись для чипа. Содержимое задачи
 * или доски модель добирает инструментами сама — заталкивать его в контекст
 * значит слать в Gemini то, о чём не спрашивали.
 */
export interface AssistantContext {
  type: AssistantContextType
  /** Ключ задачи, ключ проекта или идентификатор страницы; пусто для NONE */
  key: string
  /** Что показать в чипе контекста: «Разработка · Доска» */
  label: string
}

export const emptyContext = (): AssistantContext => ({
  type: TrackerAssistantContextType.AssistantContextTypeNone,
  key: '',
  label: '',
})

export const createThread = async (context: AssistantContext): Promise<string> => {
  const { data } = await assistantApi.assistantServiceCreateThread({
    contextType: context.type,
    contextKey: context.key,
  })

  return data.id ?? ''
}

/** Разговор в списке истории: заголовок берётся из первого вопроса. */
export interface ThreadBrief {
  id: string
  title: string
  updatedAt: string
}

/**
 * Прошлые разговоры об этом же месте.
 *
 * Отбор по контексту, а не весь список: на доске проекта интересны разговоры о
 * ней, и мешать их с обсуждением чужой задачи значит сделать переключатель
 * бесполезным.
 */
export const listThreads = async (context: AssistantContext): Promise<ThreadBrief[]> => {
  const { data } = await assistantApi.assistantServiceListThreads(context.type, context.key, 20)

  return (data.threads ?? []).map((thread) => ({
    id: thread.id ?? '',
    title: thread.title ?? '',
    updatedAt: thread.updatedAt ?? '',
  }))
}

/** Реплика разговора в том виде, в каком её показывает панель. */
export interface ThreadMessage {
  id: number
  role: 'user' | 'assistant'
  text: string
}

/**
 * Сообщения выбранного разговора.
 *
 * Строки инструментов сюда не попадают: панель каркаса показывает только текст,
 * а вызовы получат свой вид в DEV-1557.
 */
export const listMessages = async (threadId: string): Promise<ThreadMessage[]> => {
  const { data } = await assistantApi.assistantServiceListMessages(threadId)

  return (data.messages ?? [])
    .filter((message) => message.content && message.role !== TrackerAssistantRole.AssistantRoleTool)
    .map((message, index) => ({
      id: index + 1,
      role:
        message.role === TrackerAssistantRole.AssistantRoleUser
          ? ('user' as const)
          : ('assistant' as const),
      text: message.content ?? '',
    }))
}

/**
 * Остановить идущий ход.
 *
 * Отдельный вызов, а не расчёт на обрыв соединения: между браузером и сервисом
 * ингресс и прокси, и уход клиента долетает до серверного контекста когда
 * придётся. Пока не долетел — горят и дневной лимит человека, и квота
 * ai-gateway (DEV-1553).
 */
export const cancelTurn = async (threadId: string): Promise<void> => {
  await http.post(`${threadPath(threadId)}/cancel`)
}
